/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';

export interface ApiError {
  message: string;
  statusCode?: number;
  error?: string;
}

class ApiService {
  private axiosInstance: any;  

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: import.meta.env.VITE_API_BASEURL || 'https://rmsapi.idabtech.com/api/v1',
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config: any) => {
        // Log API request
        console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
          baseURL: config.baseURL,
          params: config.params,
          headers: {
            'Content-Type': config.headers['Content-Type'],
            'X-Requested-With': config.headers['X-Requested-With'],
          },
          withCredentials: config.withCredentials,
          dataType: config.data instanceof FormData ? 'FormData' : typeof config.data,
        });

        // Add Bearer token from localStorage if available (backup for cookie auth)
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // If the data is FormData, delete the Content-Type header
        // to let the browser set it automatically with the boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        return config;
      },
      (error: any) => {
        console.log('❌ API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: any) => {
        // Log successful API response
        console.log(`✅ API Response: ${response.config?.method?.toUpperCase()} ${response.config?.url}`, {
          status: response.status,
          statusText: response.statusText,
          dataType: typeof response.data,
          responseSize: JSON.stringify(response.data || {}).length,
        });
        return response;
      },
      async (error: any) => {
        const originalRequest = error.config as any & { _retry?: boolean };

        // Don't attempt to refresh for these paths
        const noRefreshPaths = [
          '/auth/login',
          '/auth/logout',
          '/auth/refresh',
          '/auth/verify-email',
          '/auth/2fa/validate',
          '/auth/2fa/setup-initial',
          '/auth/2fa/verify',
          '/auth/2fa/verify-initial',
          '/auth/resend-email-otp'
        ];

        const shouldSkipRefresh = noRefreshPaths.some(path => 
          originalRequest.url?.includes(path)
        );

        // Log API error
        console.log(`❌ API Error: ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`, {
          status: error.response?.status,
          statusText: error.response?.statusText,
          errorMessage: error.message,
          responseData: error.response?.data,
          retry: originalRequest._retry || false,
        });

        // Handle 401 errors (token expired)
        if (error.response?.status === 401 && !originalRequest._retry && !shouldSkipRefresh) {
          console.log('🔄 API Service: Handling 401 error, checking if refresh is needed...');

          // Check if we're on a public page
          const publicPaths = ['/', '/verify-email', '/email-verified', '/two-factor-auth', '/two-factor-setup'];
          const isPublicPath = publicPaths.some(path => window.location.pathname === path);

          if (isPublicPath) {
            console.log('🚫 API Service: On public page, skipping token refresh');
            // On public pages, just reject without refresh attempt
            return Promise.reject(error);
          }
          
          originalRequest._retry = true;

          try {
            console.log('🔄 API Service: Attempting token refresh...');
            // Try to refresh the token
            await this.post('/auth/refresh');
            console.log('✅ API Service: Token refresh successful, retrying original request');
            // Retry the original request
            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            console.log('❌ API Service: Token refresh failed, redirecting to login', {
              error: refreshError instanceof Error ? refreshError.message : 'Unknown error',
              status: (refreshError as any)?.response?.status
            });
            // Refresh failed, redirect to login
            window.location.href = '/';
            return Promise.reject(refreshError);
          }
        }

        // Handle other errors
        const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
        return Promise.reject({
          message: errorMessage,
          statusCode: error.response?.status,
          error: error.response?.data?.error,
        } as ApiError);
      }
    );
  }

  // HTTP methods
  async get<T = any>(url: string, params?: any): Promise<T> {
    const response = await this.axiosInstance.get(url, { params });
    return response.data as T;
  }

  async post<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.axiosInstance.post(url, data);
    return response.data as T;
  }

  async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.axiosInstance.put(url, data);
    return response.data as T;
  }

  async patch<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.axiosInstance.patch(url, data);
    return response.data as T;
  }

  async delete<T = any>(url: string): Promise<T> {
    const response = await this.axiosInstance.delete(url);
    return response.data as T;
  }

  // Getter to access axios instance for special cases like file downloads
  get axios() {
    return this.axiosInstance;
  }
}

// Export singleton instance
export const apiService = new ApiService();

// Helper to add the API prefix
const apiPath = (path: string) => `${path}`;

// Export specific API methods for authentication
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await apiService.post<any>('/auth/login', { email, password });
    
    // If login is successful and returns user data, store it
    if (response.user && response.permissions) {
      const userData = {
        ...response.user,
        isSuperAdmin: response.user.id === 1,
        permissions: response.permissions,
        roles: response.roles
      };
      localStorage.setItem('user', JSON.stringify(userData));
    }
    
    return response;
  },
  
  logout: async () => {
    const response = await apiService.post('/auth/logout');
    // Clear local storage
    localStorage.removeItem('user');
    // Force reload to clear any cached auth state
    window.location.href = '/';
    return response;
  },
  
  getCurrentUser: () => 
    apiService.get<{ admin: { id: number; email: string } }>('/auth/me'),
  
  refresh: () => 
    apiService.post('/auth/refresh'),
  
  verifyEmail: (email: string, otp: string) => 
    apiService.post<{ message: string; emailVerified: boolean; requiresTwoFactorSetup: boolean; requiresTwoFactorAuth: boolean; }>('/auth/verify-email', { email, otp }),
  
  resendEmailOtp: (email: string) => 
    apiService.post('/auth/resend-email-otp', { email }),
  
  setup2FA: () => {
    // The backend will handle authentication via cookies
    return apiService.post<{ secret: string; qrCode: string }>('/auth/2fa/setup-initial', {});
  },

  setup2FAAuthenticated: () => {
    // For already authenticated users (from settings page)
    return apiService.post<{ secret: string; qrCode: string }>('/auth/2fa/setup', {});
  },
  
  verify2FA: (token: string) => {
    // The backend will determine the correct endpoint based on cookies
    return apiService.post('/auth/2fa/verify-initial', { token });
  },

  verify2FAAuthenticated: (token: string) => {
    // For already authenticated users (from settings page)
    return apiService.post('/auth/2fa/verify', { token });
  },
  
  validate2FA: (token: string) => {
    // The backend will handle credentials via secure cookies
    return apiService.post('/auth/2fa/validate', { token });
  },

  // Email change endpoints
  initiateEmailChange: (newEmail: string) =>
    apiService.post<{ message: string }>('/auth/change-email/initiate', { newEmail }),

  verifyOldEmail: (otp: string) =>
    apiService.post<{ message: string }>('/auth/change-email/verify-old', { otp }),

  verifyNewEmail: (otp: string) =>
    apiService.post<{ message: string; temporaryPasswordSent: boolean }>('/auth/change-email/verify-new', { otp }),

  // Password change endpoint
  changePassword: (currentPassword: string, newPassword: string, twoFactorToken: string) =>
    apiService.post<{ message: string }>('/auth/change-password', { 
      currentPassword, 
      newPassword, 
      twoFactorToken 
    }),

  // 2FA management endpoints
  disable2FA: (password: string, twoFactorToken: string) =>
    apiService.post<{ message: string; twoFactorEnabled: boolean }>('/auth/2fa/disable', {
      password,
      twoFactorToken
    }),

  getRecoveryCodes: () =>
    apiService.get<{ recoveryCodes: string[] }>('/auth/2fa/recovery-codes'),

  regenerateRecoveryCodes: (password: string, twoFactorToken: string) =>
    apiService.post<{ message: string; recoveryCodes: string[] }>('/auth/2fa/recovery-codes/regenerate', {
      password,
      twoFactorToken
    }),

  validateRecoveryCode: (recoveryCode: string) =>
    apiService.post<{ message: string; admin: { id: number; email: string } }>('/auth/2fa/validate-recovery', {
      recoveryCode
    }),

  // Email validation endpoint
  checkEmail: (email: string) =>
    apiService.post<{ exists: boolean; verified: boolean; message: string }>('/auth/check-email', { email }),

  // Forgot Password endpoints
  forgotPassword: (email: string) =>
    apiService.post<{ success: boolean; message: string; emailExists: boolean }>('/auth/forgot-password', { email }),

  resetPassword: (token: string, email: string, newPassword: string) =>
    apiService.post<{ message: string }>('/auth/reset-password', {
      token,
      email,
      newPassword
    }),
};

// Generic API methods for all other endpoints
export const api = {
  // Company endpoints
  company: {
    count: (params?: any) => apiService.get(apiPath('/company/count'), params),
    countServiceProviders: () => apiService.get(apiPath('/company/count/service-providers')),
    countGeneral: () => apiService.get(apiPath('/company/count/general')),
    getAll: (params?: any) => apiService.get(apiPath('/company'), params),
    getById: (id: string | number) => apiService.get(apiPath(`/company/${id}`)),
    create: (data: any) => apiService.post(apiPath('/company'), data),
    update: (id: string | number, data: any) => apiService.patch(apiPath(`/company/${id}`), data),
    delete: (id: string | number) => apiService.delete(apiPath(`/company/${id}`)),
    resetPassword: (id: string | number, data: any) => apiService.patch(apiPath(`/company/${id}/reset-password`), data),
  },
  
  // Candidate endpoints
  candidate: {
    count: (params?: any) => apiService.get(apiPath('/candidate/count'), params),
    countVerified: () => apiService.get(apiPath('/candidate/count/verified')),
    getAll: (params?: any) => apiService.get(apiPath('/candidate'), params),
    getById: (id: string | number) => apiService.get(apiPath(`/candidate/${id}`)),
    create: (data: any) => apiService.post(apiPath('/candidate'), {agree_to_terms:true,...data}),
    update: (id: string | number, data: any) => apiService.patch(apiPath(`/candidate/${id}`), data),
    delete: (id: string | number) => apiService.delete(apiPath(`/candidate/${id}`)),
    resetPassword: (id: string | number, data: any) => apiService.patch(apiPath(`/candidate/${id}/reset-password`), data),
  },
  
  // Job endpoints
  job: {
    count: (params?: any) => apiService.get(apiPath('/job/count'), params),
    countActive: () => apiService.get(apiPath('/job/count/active')),
    countPending: () => apiService.get(apiPath('/job/count/pending')),
    countExpired: () => apiService.get(apiPath('/job/count/expired')),
    getAll: (params?: any) => apiService.get(apiPath('/job'), params),
    getById: (id: string | number) => apiService.get(apiPath(`/job/${id}`)),
    create: (data: any) => apiService.post(apiPath('/job'), data),
    update: (id: string | number, data: any) => apiService.patch(apiPath(`/job/${id}`), data),
    delete: (id: string | number) => apiService.delete(apiPath(`/job/${id}`)),
  },
  
  // Job Category endpoints
  jobCategory: {
    getAll: (params?: any) => apiService.get(apiPath('/jobcategory'), params),
    getById: (id: string | number) => apiService.get(apiPath(`/jobcategory/${id}`)),
    create: (data: any) => apiService.post(apiPath('/jobcategory'), data),
    update: (id: string | number, data: any) => apiService.patch(apiPath(`/jobcategory/${id}`), data),
    delete: (id: string | number) => apiService.delete(apiPath(`/jobcategory/${id}`)),
  },
  
  // Job Role endpoints
  jobRole: {
    getAll: (params?: any) => apiService.get(apiPath('/jobroles'), params),
    getById: (id: string | number) => apiService.get(apiPath(`/jobroles/${id}`)),
    create: (data: any) => apiService.post(apiPath('/jobroles'), data),
    update: (id: string | number, data: any) => apiService.patch(apiPath(`/jobroles/${id}`), data),
    delete: (id: string | number) => apiService.delete(apiPath(`/jobroles/${id}`)),
  },
  
  // Price Plan endpoints
  pricePlan: {
    company: {
      getAll: (params?: any) => apiService.get(apiPath('/price-plan/company'), params),
      getById: (id: string | number) => apiService.get(apiPath(`/price-plan/company/${id}`)),
      create: (data: any) => apiService.post(apiPath('/price-plan/company'), data),
      update: (id: string | number, data: any) => apiService.patch(apiPath(`/price-plan/company/${id}`), data),
      delete: (id: string | number) => apiService.delete(apiPath(`/price-plan/company/${id}`)),
    },
    candidate: {
      getAll: (params?: any) => apiService.get(apiPath('/price-plan/candidate'), params),
      getById: (id: string | number) => apiService.get(apiPath(`/price-plan/candidate/${id}`)),
      create: (data: any) => apiService.post(apiPath('/price-plan/candidate'), data),
      update: (id: string | number, data: any) => apiService.patch(apiPath(`/price-plan/candidate/${id}`), data),
      delete: (id: string | number) => apiService.delete(apiPath(`/price-plan/candidate/${id}`)),
    },
  },
  
  // Attribute endpoints
  attribute: {
    industryType: {
      getAll: (params?: any) => apiService.get('/attributes/industry-type', params),
      getById: (id: string | number) => apiService.get(`/attributes/industry-type/${id}`),
      create: (data: any) => apiService.post('/attributes/industry-type', data),
      update: (id: string | number, data: any) => apiService.patch(`/attributes/industry-type/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/attributes/industry-type/${id}`),
    },
    profession: {
      getAll: (params?: any) => apiService.get('/attributes/profession', params),
      getById: (id: string | number) => apiService.get(`/attributes/profession/${id}`),
      create: (data: any) => apiService.post('/attributes/profession', data),
      update: (id: string | number, data: any) => apiService.patch(`/attributes/profession/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/attributes/profession/${id}`),
    },
    skill: {
      getAll: (params?: any) => apiService.get('/attributes/skill', params),
      getById: (id: string | number) => apiService.get(`/attributes/skill/${id}`),
      create: (data: any) => apiService.post('/attributes/skill', data),
      update: (id: string | number, data: any) => apiService.patch(`/attributes/skill/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/attributes/skill/${id}`),
    },
    tag: {
      getAll: (params?: any) => apiService.get('/attributes/tag', params),
      getById: (id: string | number) => apiService.get(`/attributes/tag/${id}`),
      create: (data: any) => apiService.post('/attributes/tag', data),
      update: (id: string | number, data: any) => apiService.patch(`/attributes/tag/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/attributes/tag/${id}`),
    },
    benefit: {
      getAll: (params?: any) => apiService.get('/attributes/benefit', params),
      getById: (id: string | number) => apiService.get(`/attributes/benefit/${id}`),
      create: (data: any) => apiService.post('/attributes/benefit', data),
      update: (id: string | number, data: any) => apiService.patch(`/attributes/benefit/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/attributes/benefit/${id}`),
    },
    language: {
      getAll: (params?: any) => apiService.get('/attributes/language', params),
      getById: (id: string | number) => apiService.get(`/attributes/language/${id}`),
      create: (data: any) => apiService.post('/attributes/language', data),
      update: (id: string | number, data: any) => apiService.patch(`/attributes/language/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/attributes/language/${id}`),
    },
    country: {
      getAll: (params?: any) => apiService.get('/attributes/country', params),
      getById: (id: string | number) => apiService.get(`/attributes/country/${id}`),
      create: (data: any) => apiService.post('/attributes/country', data),
      update: (id: string | number, data: any) => apiService.patch(`/attributes/country/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/attributes/country/${id}`),
    },
    organisationType: {
      getAll: (params?: any) => apiService.get('/attributes/organisation-type', params),
      getById: (id: string | number) => apiService.get(`/attributes/organisation-type/${id}`),
      create: (data: any) => apiService.post('/attributes/organisation-type', data),
      update: (id: string | number, data: any) => apiService.patch(`/attributes/organisation-type/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/attributes/organisation-type/${id}`),
      seed: () => apiService.post('/attributes/organisation-type/seed'),
    },
    timezone: {
      getAll: (params?: any) => apiService.get('/attributes/timezones', params),
      getById: (id: string | number) => apiService.get(`/attributes/timezones/${id}`),
      getByValue: (value: string) => apiService.get(`/attributes/timezones/value/${value}`),
      create: (data: any) => apiService.post('/attributes/timezones', data),
      update: (id: string | number, data: any) => apiService.patch(`/attributes/timezones/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/attributes/timezones/${id}`),
      seed: () => apiService.post('/attributes/timezones/seed'),
    },
  },
  
  // Settings endpoints
  settings: {
    general: {
      get: (id: number = 1) => apiService.get(`/settings/general/${id}`),
      create: (data: any) => apiService.post('/settings/general', data),
      update: (id: string | number, data: any) => apiService.patch(`/settings/general/${id}`, data),
    },
    language: {
      getAll: () => apiService.get('/settings/language'),
      get: (id: string | number) => apiService.get(`/settings/language/${id}`),
      getById: (id: string | number) => apiService.get(`/settings/language/${id}`),
      getAvailable: () => apiService.get('/settings/language/available'),
      create: (data: any) => apiService.post('/settings/language', data),
      update: (id: string | number, data: any) => apiService.patch(`/settings/language/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/settings/language/${id}`),
    },
    currency: {
      getAll: () => apiService.get('/settings/currency'),
      get: (id: string | number) => apiService.get(`/settings/currency/${id}`),
      getAvailable: () => apiService.get('/settings/currency/available'),
      create: (data: any) => apiService.post('/settings/currency', data),
      update: (id: string | number, data: any) => apiService.patch(`/settings/currency/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/settings/currency/${id}`),
    },
    smtp: {
      getAll: () => apiService.get('/settings/smtp'),
      get: (id: number = 1) => apiService.get(`/settings/smtp/${id}`),
      getActive: () => apiService.get('/settings/smtp/active'),
      create: (data: any) => apiService.post('/settings/smtp', data),
      update: (id: string | number, data: any) => apiService.patch(`/settings/smtp/${id}`, data),
      testMail: (email: string) => apiService.post('/settings/smtp/test-mail', { email }),
    },
    payment: {
      getAll: () => apiService.get('/settings/payment'),
      get: (type: string) => apiService.get(`/settings/payment/${type}`),
      update: (type: string, data: any) => apiService.patch(`/settings/payment/${type}`, data),
    },
    emailTemplates: {
      getAll: () => apiService.get('/settings/email-templates'),
      get: (id: string | number) => apiService.get(`/settings/email-templates/${id}`),
      create: (data: any) => apiService.post('/settings/email-templates', data),
      update: (id: string | number, data: any) => apiService.patch(`/settings/email-templates/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/settings/email-templates/${id}`),
      getVariables: (id: string | number) => apiService.get(`/settings/email-templates/${id}/variables`),
    },
    getTimezone: () => apiService.get(apiPath('/settings/timezone')),
    updateTimezone: (data: { timezone: string }) => apiService.patch(apiPath('/settings/timezone'), data),
    getAllSettings: () => apiService.get(apiPath('/settings')),
    updateSettings: (data: any) => apiService.patch(apiPath('/settings'), data),
  },
  
  // Dashboard endpoints (these might not exist in backend yet)
  dashboard: {
    getStats: () => apiService.get(apiPath('/dashboard/stats')),
    getCharts: () => apiService.get(apiPath('/dashboard/charts')),
    getRevenueStats: () => apiService.get(apiPath('/dashboard/revenue-stats')),
    getSubscriberStats: () => apiService.get(apiPath('/dashboard/subscriber-stats')),
    getUserStats: () => apiService.get(apiPath('/dashboard/user-stats')),
    getRecentJobs: (limit: number = 5) => apiService.get(apiPath(`/dashboard/recent-jobs?limit=${limit}`)),
    getRecentOrders: (limit: number = 5) => apiService.get(apiPath(`/dashboard/recent-orders?limit=${limit}`)),
  },
  
  // File upload
  upload: {
    image: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiService.post(apiPath('/upload/image'), formData);
    },
    document: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiService.post(apiPath('/upload/document'), formData);
    },
  },

  // User endpoints
  user: {
    getAll: (params?: any) => apiService.get(apiPath('/users'), params),
    getById: (id: string | number) => apiService.get(apiPath(`/users/${id}`)),
    create: (data: any) => apiService.post(apiPath('/users'), data),
    update: (id: string | number, data: any) => apiService.patch(apiPath(`/users/${id}`), data),
    delete: (id: string | number) => apiService.delete(apiPath(`/users/${id}`)),
    changePassword: (id: string | number, data: any) => apiService.patch(apiPath(`/users/${id}/change-password`), data),
    toggleStatus: (id: string | number) => apiService.patch(apiPath(`/users/${id}/toggle-status`), {}),
    getRole: (id: string | number) => apiService.get(apiPath(`/users/${id}/role`)),
  },

  // Admin user management endpoints
  admin: {
    getAll: (params?: any) => apiService.get(apiPath('/admin'), params),
    getById: (id: string | number) => apiService.get(apiPath(`/admin/${id}`)),
    create: (data: any) => apiService.post(apiPath('/admin'), data),
    update: (id: string | number, data: any) => apiService.patch(apiPath(`/admin/${id}`), data),
    delete: (id: string | number) => apiService.delete(apiPath(`/admin/${id}`)),
    toggleStatus: (id: string | number) => apiService.patch(apiPath(`/admin/${id}/toggle-status`), {}),
    getRoles: (id: string | number) => apiService.get(apiPath(`/admin/${id}/roles`)),
    assignRole: (id: string | number, roleId: number) => apiService.post(apiPath(`/admin/${id}/assign-role`), { roleId }),
    removeRole: (id: string | number, roleId: number) => apiService.delete(apiPath(`/admin/${id}/remove-role/${roleId}`)),
  },

  // Role and Permission endpoints
  role: {
    getAll: (params?: any) => apiService.get(apiPath('/roles'), params),
    getById: (id: string | number) => apiService.get(apiPath(`/roles/${id}`)),
    create: (data: any) => apiService.post(apiPath('/roles'), data),
    update: (id: string | number, data: any) => apiService.patch(apiPath(`/roles/${id}`), data),
    delete: (id: string | number) => apiService.delete(apiPath(`/roles/${id}`)),
    assignToUser: (userId: number, roleId: number) => apiService.post(apiPath(`/roles/${roleId}/assign-user/${userId}`), {}),
    removeFromUser: (userId: number, roleId: number) => apiService.delete(apiPath(`/roles/${roleId}/remove-user/${userId}`)),
    getUserRoles: (userId: number) => apiService.get(apiPath(`/roles/user/${userId}`)),
    getUserPermissions: (userId: number) => apiService.get(apiPath(`/roles/user/${userId}/permissions`)),
    getRoleUsers: (roleId: number) => apiService.get(apiPath(`/roles/${roleId}/users`)),
    getModules: () => apiService.get(apiPath('/roles/modules/available')),
  },
  
  // Payment endpoints
  payment: {
    stripe: {
      createSession: (data: any) => apiService.post('/auto-payment/stripe/create-session', data),
      webhook: (data: any) => apiService.post('/auto-payment/stripe/webhook', data),
    },
    paypal: {
      createOrder: (data: any) => apiService.post('/auto-payment/paypal/create-order', data),
      captureOrder: (orderId: string) => apiService.post(`/auto-payment/paypal/capture-order/${orderId}`, {}),
    },
    razorpay: {
      createOrder: (data: any) => apiService.post('/auto-payment/razorpay/create-order', data),
      verifyPayment: (data: any) => apiService.post('/auto-payment/razorpay/verify-payment', data),
    },
    // Add other payment gateways as needed
  },

  // Auto-payment settings endpoints
  autoPayment: {
    paypal: {
      getAll: () => apiService.get(`/auto-payment/paypal/1`),
      create: (data: any) => apiService.post('/auto-payment/paypal', data),
      update: ( data: any) => apiService.patch(`/auto-payment/paypal/1`, data),
    },
    stripe: {
      getAll: () => apiService.get(`/auto-payment/stripe/1`),
      create: (data: any) => apiService.post('/auto-payment/stripe', data),
      update: ( data: any) => apiService.patch(`/auto-payment/stripe/1`, data),
    },
    razorpay: {
      getAll: () => apiService.get(`/auto-payment/razorpay/1`),
      create: (data: any) => apiService.post('/auto-payment/razorpay', data),
      update: ( data: any) => apiService.patch(`/auto-payment/razorpay/1`, data),
    },
    'ssi-commerz': {
      getAll: () => apiService.get(`/auto-payment/ssi-commerz/1`),
      create: (data: any) => apiService.post('/auto-payment/ssi-commerz', data),
      update: ( data: any) => apiService.patch(`/auto-payment/ssi-commerz/1`, data),
    },
    flutterwave: {
      getAll: () => apiService.get(`/auto-payment/flutterwave/1`),
      create: (data: any) => apiService.post('/auto-payment/flutterwave', data),
      update: ( data: any) => apiService.patch(`/auto-payment/flutterwave/1`, data),
    },
    mollie: {
      getAll: () => apiService.get(`/auto-payment/mollie/1`),
      create: (data: any) => apiService.post('/auto-payment/mollie', data),
      update: ( data: any) => apiService.patch(`/auto-payment/mollie/1`, data),
    },
    paystack: {
      getAll: () => apiService.get(`/auto-payment/paystack/1`),
      create: (data: any) => apiService.post('/auto-payment/paystack', data),
      update: ( data: any) => apiService.patch(`/auto-payment/paystack/1`, data),
    },
    midtrans: {
      getAll: () => apiService.get(`/auto-payment/midtrans/1`),
      create: (data: any) => apiService.post('/auto-payment/midtrans', data),
      update: ( data: any) => apiService.patch(`/auto-payment/midtrans/1`, data),
    },
    instamojo: {
      getAll: () => apiService.get(`/auto-payment/instamojo/1`),
      create: (data: any) => apiService.post('/auto-payment/instamojo', data),
      update: ( data: any) => apiService.patch(`/auto-payment/instamojo/1`, data),
    },
  },
  
  // Order endpoints
  order: {
    getAll: (params?: any) => apiService.get('/orders', params),
    getById: (id: string | number) => apiService.get(`/orders/${id}`),
    create: (data: any) => apiService.post('/orders', data),
    update: (id: string | number, data: any) => apiService.patch(`/orders/${id}`, data),
    delete: (id: string | number) => apiService.delete(`/orders/${id}`),
    getStats: () => apiService.get('/orders/stats'),
    refund: (id: string | number, data: any) => apiService.post(`/orders/${id}/refund`, data),
    getInvoice: (paymentId: string) => apiService.get(`/order-invoice/${paymentId}/view`),
    downloadInvoice: async (paymentId: string) => {
      // Use raw axios for file download to handle response properly
      return await apiService.axios.get(`/order-invoice/${paymentId}/download`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf',
        },
      });
    },
  },
  
  // Manual Payment endpoints
  manualPayment: {
    getAll: () => apiService.get('/manual-payment'),
    getById: (id: string | number) => apiService.get(`/manual-payment/${id}`),
    create: (data: any) => apiService.post('/manual-payment', data),
    update: (id: string | number, data: any) => apiService.put(`/manual-payment/${id}`, data),
    delete: (id: string | number) => apiService.delete(`/manual-payment/${id}`),
    toggleStatus: (id: string | number, isActive: boolean) => apiService.patch(`/manual-payment/${id}/toggle-status`, { isActive }),
    getActive: () => apiService.get('/manual-payment/active'),
    generateQR: (paymentData: string) => apiService.post('/manual-payment/generate-qr', { paymentData }),
    generatePaymentLink: (orderId: number, amount: number, currency: string) => 
      apiService.post('/manual-payment/generate-payment-link', { orderId, amount, currency }),
    recordTransaction: (data: any) => apiService.post('/manual-payment/record-transaction', data),
  },
  
  analytics: {
    getDashboardStats: (params?: any) => apiService.get('/analytics/dashboard-stats', params),
    getRecentActivity: (params?: any) => apiService.get('/analytics/recent-activity', params),
    getRevenueStats: (params?: any) => apiService.get('/analytics/revenue', params),
    getCategoryStats: (params?: any) => apiService.get('/analytics/categories', params),
  },
  
  // Activity endpoints
  activity: {
    getAll: (params?: any) => apiService.get(apiPath('/activity'), params),
    getRecent: (limit?: number) => apiService.get(apiPath(`/activity/recent${limit ? `?limit=${limit}` : ''}`)),
    getStats: () => apiService.get(apiPath('/activity/stats')),
  },
  
  // Notifications endpoints
  notifications: {
    create: (data: any) => apiService.post('/notifications', data),
    getAll: (params?: any) => apiService.get('/notifications', params),
    getById: (id: string | number) => apiService.get(`/notifications/${id}`),
    getUnreadCount: () => apiService.get('/notifications/unread-count'),
    markAsRead: (notificationIds: number[]) => apiService.post('/notifications/mark-as-read', { notificationIds }),
    markAllAsRead: () => apiService.post('/notifications/mark-all-as-read', {}),
    archive: (notificationIds: number[]) => apiService.post('/notifications/archive', { notificationIds }),
    delete: (id: string | number) => apiService.delete(`/notifications/${id}`),
  },

  // Cookie Consent endpoints
  cookieConsent: {
    // Public endpoint for frontend
    getConsentData: () => apiService.get('/cookie-consent/public/consent-data'),
    
    // Admin endpoints
    categories: {
      getAll: () => apiService.get('/cookie-consent/categories'),
      getById: (id: string | number) => apiService.get(`/cookie-consent/categories/${id}`),
      create: (data: any) => apiService.post('/cookie-consent/categories', data),
      update: (id: string | number, data: any) => apiService.put(`/cookie-consent/categories/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/cookie-consent/categories/${id}`),
    },
    items: {
      getAll: () => apiService.get('/cookie-consent/items'),
      getById: (id: string | number) => apiService.get(`/cookie-consent/items/${id}`),
      getByCategory: (categoryId: string | number) => apiService.get(`/cookie-consent/items/category/${categoryId}`),
      create: (data: any) => apiService.post('/cookie-consent/items', data),
      update: (id: string | number, data: any) => apiService.put(`/cookie-consent/items/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/cookie-consent/items/${id}`),
    },
    settings: {
      get: () => apiService.get('/cookie-consent/settings'),
      update: (data: any) => apiService.put('/cookie-consent/settings', data),
    },
    logs: {
      getAll: (params?: { page?: number; limit?: number; startDate?: string; endDate?: string }) => 
        apiService.get('/cookie-consent/logs', { params }),
      create: (data: any) => apiService.post('/cookie-consent/logs', data),
      getStatistics: (params?: { startDate?: string; endDate?: string }) => 
        apiService.get('/cookie-consent/logs/statistics', { params }),
    },
    scripts: {
      getAll: () => apiService.get('/cookie-consent/scripts'),
      getById: (id: string | number) => apiService.get(`/cookie-consent/scripts/${id}`),
      getByCategory: (categoryId: string | number) => apiService.get(`/cookie-consent/scripts/category/${categoryId}`),
      create: (data: any) => apiService.post('/cookie-consent/scripts', data),
      update: (id: string | number, data: any) => apiService.patch(`/cookie-consent/scripts/${id}`, data),
      delete: (id: string | number) => apiService.delete(`/cookie-consent/scripts/${id}`),
      getContent: (categories: string[]) => 
        apiService.get('/cookie-consent/scripts/content', { params: { categories: categories.join(',') } }),
    },
    initializeDefaults: () => apiService.post('/cookie-consent/initialize-defaults', {}),
  },

};